import styled from "@emotion/styled"
import { Button, Typography } from "@mui/material"
import { useTranslations } from "next-intl"
import { useInView } from "react-intersection-observer"
import { CustomImage, transitionDelayTime } from "ui"
import { sharedColors } from "ui/theme/colors"
import { TransitionUp } from "ui/transitions/TransitionUp"
import { useInViewConfig } from "ui/transitions/utils"
import { ArticlesData, getNonUmbracoUrl, Link } from "utils"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"

export const formatDate = (dateString: string) => {
  if (!dateString) return ""
  const date = new Date(dateString)
  return date
    .toLocaleDateString("en-GB", {
      day: "2-digit",
      month: "2-digit",
      year: "2-digit",
    })
    .replace(/\//g, "/")
}

const OuterLayout = styled.div`
  width: 100%;
  display: flex;
`

const StyledTeamGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  column-gap: 32px;
  row-gap: 32px;

  width: 100%;
  ${({ theme }) => theme.breakpoints.up("md")} {
    grid-template-columns: repeat(3, minmax(0, 352px));
    justify-content: center;
    column-gap: 24px;
    row-gap: 48px;
  }
`

const StyledItem = styled.div`
  display: flex;
  padding: 16px;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  height: 100%;
  border-radius: 12px;
  box-shadow: 0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  transition: all 0.3s ease-in-out;

  :hover {
    /* border: 1px solid var(--Grey-Grey, #6f747d); */
    box-shadow:
      inset 0px 0px 0px 1px #6f747d,
      0px 2px 8px 0px rgba(38, 48, 61, 0.16);
  }
`

const StyledImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: 144px;
  border-radius: 8px;
  overflow: hidden;

  img {
    transition: transform 0.3s ease-in-out;
  }

  ${StyledItem}:hover & img {
    transform: scale(1.2);
  }
`

const TextBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
`

const TitleText = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 1;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const DescriptionText = styled(Typography)`
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  text-overflow: ellipsis;
`

const StyledDate = styled(Typography)`
  margin-top: auto;
  ${({ theme }) => theme.breakpoints.up("md")} {
    line-height: 24px;
  }
`

const StyledButton = styled(Button)`
  margin-top: auto;
`

export default function ArticlesGrid({ data }: { data: ArticlesData }) {
  const t = useTranslations("Homepage")

  const { ref, inView } = useInView(useInViewConfig)
  return (
    <OuterLayout ref={ref}>
      <TransitionUp $inView={inView} $delay={transitionDelayTime}>
        <StyledTeamGrid>
          {data.articles.map((el, i) => (
            <StyledItem key={i}>
              <StyledImageContainer>
                <CustomImage
                  src={getUmbracoMediaUrl(el.image)}
                  alt={el.title}
                  fill
                  style={{ objectFit: "cover" }}
                />
              </StyledImageContainer>
              <TextBox>
                <TitleText variant="subtitle1" component={"h3"}>
                  {el.title}
                </TitleText>
                <DescriptionText variant="body2">{el.descr}</DescriptionText>
                <StyledDate variant="body3" color={sharedColors.labelGray}>
                  {formatDate(el.date)}
                </StyledDate>
              </TextBox>
              <StyledButton
                variant="secondary"
                href={getNonUmbracoUrl(el.url)}
                LinkComponent={Link}
              >
                {t("forMoreInformat")}
              </StyledButton>
            </StyledItem>
          ))}
        </StyledTeamGrid>
      </TransitionUp>
    </OuterLayout>
  )
}
